import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePopper } from '@wix/thunderbolt-elements/src/providers/usePopper/usePopper';

import { getPopperConfig } from './utils';

export type UseMenuPositioningOptions = {
  isMenuOpen: boolean;
  rootElement: HTMLDivElement | null;
  referenceElement: HTMLDivElement | null;
  popperElement: HTMLDivElement | null;
  useNewCustomMenuPositioning: boolean;
};

export const useMenuPositioning = ({
  isMenuOpen,
  rootElement,
  referenceElement,
  popperElement,
  useNewCustomMenuPositioning,
}: UseMenuPositioningOptions) => {
  const [isVisible, setIsVisible] = useState(false);

  const popperConfig = useMemo(
    () =>
      getPopperConfig({
        rootElement,
        referenceElement,
      }),
    [rootElement, referenceElement],
  );

  const { setPopper, setRef, styles } = usePopper(popperConfig);

  const handleMenuOpen = useCallback(() => {
    if (!popperElement) {
      return;
    }

    setRef(referenceElement);
    setPopper(popperElement);
    setIsVisible(true);
  }, [popperElement, referenceElement, setPopper, setRef]);

  const handleMenuClose = useCallback(() => {
    setRef(null);
    setPopper(null);
    setIsVisible(false);
  }, [setPopper, setRef]);

  useEffect(() => {
    if (!useNewCustomMenuPositioning) {
      return;
    }

    isMenuOpen ? handleMenuOpen() : handleMenuClose();
  }, [
    isMenuOpen,
    useNewCustomMenuPositioning,
    handleMenuOpen,
    handleMenuClose,
  ]);

  return {
    menuStyles: useNewCustomMenuPositioning
      ? {
          ...styles.popper,
          left: undefined, // removing "left" here to ensure we always have correct menu dimensions for custom positioning
        }
      : undefined,
    isVisible: useNewCustomMenuPositioning ? isVisible : undefined,
  };
};
